import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Userfront from '../auth/userfrontCompat';
import api from './../api/axios';
import { useNotification } from './NotificationContext';
import { useSidebar } from './SidebarContext';
import { useTranslation } from 'react-i18next';
import {
  FaChevronDown, FaSignOutAlt, FaQuestion, FaHome, FaChartBar,
  FaEnvelope, FaBriefcase, FaCog, FaUsers, FaCreditCard, FaBars
} from 'react-icons/fa';
import placeholderImage from './../assets/placeholder-image.png';

const NavBar = () => {
  const { t, i18n } = useTranslation();
  const { collapsed } = useSidebar();
  const { notifications } = useNotification();
  const [user, setUser] = useState(null);
  const [hasPaidTeamAccess, setHasPaidTeamAccess] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef();
  const toggleButtonRef = useRef();

  // Build the help-center URL based on language
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
  const helpUrl = `http://localhost:4321${lang.startsWith('nl') ? '/nl' : ''}/help-center`;

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userEmail = Userfront.user?.email;
        if (!userEmail) return;
        const [userResponse, accessResponse] = await Promise.all([
          api.get('/user/user'),
          api.get('/subscription/access-status').catch(() => ({ data: { hasAccess: false, trialActive: false } })),
        ]);
        const currentUser = userResponse.data.user;
        setUser(currentUser);
        setHasPaidTeamAccess(
          currentUser?.role === 'Admin' &&
          currentUser?.subscriptionType === 'Team' &&
          accessResponse.data?.hasAccess === true &&
          accessResponse.data?.trialActive !== true
        );
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleLogout() {
    try {
      await Userfront.logout();
    } catch (_) {
      // ignore
    } finally {
      try { await api.post('/auth/logout'); } catch (_) {}
      window.location.href = '/login';
    }
  }

  const isAdmin = user?.role === 'Admin';
  const profileImageUrl = user?.profileImageUrl || Userfront.user?.image || placeholderImage;

  return (
    <header
      className={`fixed top-0 right-0 h-16 w-full bg-white flex items-center justify-between px-4 sm:px-4 z-40 transition-all
        ${collapsed ? 'lg:ml-16 lg:w-[calc(100%-4rem)]' : 'lg:ml-64 lg:w-[calc(100%-16rem)]'}`}
    >
      {/* Mobile Logo */}
      <div className="flex items-center gap-2 lg:hidden absolute left-4">
        <Link to="/" className="flex items-center">
          <img src="/logo.png" alt="Logo" className="h-8 w-8" />
          <span className="ml-2 font-bold text-gray-800">Leadgenflow</span>
        </Link>
      </div>

      {/* Notifications */}
      <div className="hidden lg:flex flex-1 justify-start space-x-3">
        {notifications?.length > 0 &&
          notifications.map((n) => {
            const typeClass =
              n.type === 'success'
                ? 'bg-green-100 text-green-800'
                : n.type === 'info'
                ? 'bg-blue-100 text-blue-800'
                : n.type === 'warning'
                ? 'bg-amber-100 text-amber-800'
                : 'bg-red-100 text-red-800';

            return (
              <div
                key={n.id}
                className={`text-sm px-3 py-1 rounded ${typeClass}`}
              >
                {n.message}
              </div>
            );
          })}
      </div>

      {/* Right */}
      <div className="flex items-center space-x-4 relative" style={{ marginRight: '-1rem', marginLeft: 'auto' }}>
        <a
          href={helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:flex items-center text-sm text-blue-600 hover:underline"
        >
          <FaQuestion className="mr-1" /> {t('navbar.helpCenter')}
        </a>
        <button
          ref={toggleButtonRef}
          onClick={() => setShowDropdown((prev) => !prev)}
          className="flex items-center gap-2 hover:bg-gray-100 px-2 py-2 rounded focus:outline-none"
          aria-expanded={showDropdown}
        >
          <div className="hidden md:block text-sm text-right">
            <div className="font-medium text-gray-900">{user?.name}</div>
            <div className="text-gray-500 text-xs">{user?.role}</div>
          </div>
          <img
            src={profileImageUrl}
            alt="User"
            className="w-8 h-8 rounded-full object-cover border border-gray-300"
          />
          <span className="text-gray-500 text-sm">
            <FaChevronDown className="hidden lg:inline" />
            <FaBars className="inline lg:hidden" />
          </span>
        </button>

        {/* Dropdown */}
        <div
          ref={dropdownRef}
          className={`absolute top-14 right-0 w-64 bg-white shadow-lg rounded-lg z-50 p-4 text-sm transform transition-all duration-200 ease-out ${
            showDropdown ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
          }`}
        >
          <div className="mb-2">
            <div className="font-semibold text-gray-900">{user?.name}</div>
            <div className="text-gray-500 text-xs">{user?.role}</div>
          </div>
          <hr className="my-2" />
          <nav className="flex flex-col gap-4">
            <Link to="/" onClick={() => setShowDropdown(false)} className="hover:text-blue-600">
              <FaHome className="inline mr-2" /> {t('navbar.dashboard')}
            </Link>
            <Link to="/campaigns" onClick={() => setShowDropdown(false)} className="hover:text-blue-600">
              <FaChartBar className="inline mr-2" /> {t('navbar.campaigns')}
            </Link>
            <Link to="/messages" onClick={() => setShowDropdown(false)} className="hover:text-blue-600">
              <FaEnvelope className="inline mr-2" /> {t('navbar.messages')}
            </Link>
            <Link to="/leads" onClick={() => setShowDropdown(false)} className="hover:text-blue-600">
              <FaBriefcase className="inline mr-2" /> {t('navbar.leads')}
            </Link>
            <Link to="/settings" onClick={() => setShowDropdown(false)} className="hover:text-blue-600">
              <FaCog className="inline mr-2" /> {t('navbar.settings')}
            </Link>
            {hasPaidTeamAccess && (
              <Link to="/team" onClick={() => setShowDropdown(false)} className="hover:text-blue-600">
                <FaUsers className="inline mr-2" /> {t('navbar.team')}
              </Link>
            )}
            {isAdmin && (
              <Link to="/subscription" onClick={() => setShowDropdown(false)} className="hover:text-blue-600">
                <FaCreditCard className="inline mr-2" /> {t('navbar.billing')}
              </Link>
            )}
            <hr className="my-2" />
            <button onClick={handleLogout} className="flex items-center hover:text-blue-600">
              <FaSignOutAlt className="mr-2" /> {t('navbar.logout')}
            </button>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default NavBar;
